import React, { Fragment, FunctionComponent, useMemo } from "react";
import { Icon, styled } from "@mui/material";
import { LayoutComponent } from "../../../builder/Layout";
import { Position } from "../../../types/theme";
import type { Settings as MoreInformationSettings } from "../MoreInformation";
import type { Settings as HowItWorkSettings } from "../HowItWorks";
import type { Settings as FeatureSettings } from "../Features";
import { Container } from "../../ui/DefaultPageContainer";
import { PublicComponentProps } from "../PublicComponentProps";
import type { Item } from "../../../dataAccess/api/item";
import {
  DescriptionText,
  NameContainer,
  StyledName,
} from "../../ui/DealLikeComponents";
import { useTranslation } from "../../../helpers/i18n";

export interface Subcomponents {
  Quote: LayoutComponent<unknown>;
  Deal: LayoutComponent<unknown>;
  Features?: LayoutComponent<FeatureSettings>;
  Gallery?: LayoutComponent<unknown>;
  HowItWorks?: LayoutComponent<HowItWorkSettings>;
  Reviews?: LayoutComponent<unknown>;
  Pricing?: LayoutComponent<unknown>;
  MoreInformation?: LayoutComponent<MoreInformationSettings>;
  CustomersWhoUse?: LayoutComponent<unknown>;
  SimilarTo?: LayoutComponent<unknown>;
}

export const dealLayoutSideContentWidth = "23rem";

const margin = 4;

const Main = styled("div")<{
  placement?: Position;
}>(({ placement, theme }) => ({
  marginTop: theme.spacing(margin),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(margin),
  [theme.breakpoints.up("md")]: {
    height: "100%",
    display: "grid",
    gridGap: theme.defaultSpacing.md.page.content,
    paddingLeft: theme.defaultSpacing.md.page.edges,
    paddingRight: theme.defaultSpacing.md.page.edges,
    gridTemplateColumns:
      placement === "vertical"
        ? `${dealLayoutSideContentWidth} minmax(0, 1fr)`
        : "minmax(0, 1fr)",
  },
}));

const EmptyState = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  maxWidth: 900,
  height: 300,
  marginTop: theme.spacing(margin),
  gap: theme.spacing(1),
  [theme.breakpoints.up("md")]: {
    height: 400,
    paddingLeft: theme.defaultSpacing.md.page.edges,
    paddingRight: theme.defaultSpacing.md.page.edges,
  },
}));

interface Props
  extends PublicComponentProps<Record<string, unknown>, Subcomponents> {
  item?: Item;
  loadingItem?: boolean;
}

const DealDetailsLayout: FunctionComponent<Props> = ({
  subComponents,
  item,
  loadingItem,
}) => {
  const { t } = useTranslation();
  const content = useMemo(() => {
    if (loadingItem) {
      return <EmptyState />;
    }
    if (!item?.id) {
      return (
        <EmptyState>
          <NameContainer>
            <Icon color="warning">warning</Icon>
            <StyledName size="medium">
              {t("information_listing_not_available_title")}
            </StyledName>
          </NameContainer>
          <DescriptionText>
            {t("information_listing_not_available_description")}
          </DescriptionText>
        </EmptyState>
      );
    }
    return (
      <Main placement={subComponents?.MoreInformation?.settings.placement}>
        {subComponents &&
          Object.values(subComponents).map((value, idx) => (
            <Fragment key={idx}>{value.component}</Fragment>
          ))}
      </Main>
    );
  }, [subComponents, loadingItem, item, t]);
  return <Container>{content}</Container>;
};

export default DealDetailsLayout;
